/**
 * accuracyPlaceholder.ts
 * ----------------------
 * Synthetic AccuracyNode data, so AccuracyBox can be reviewed visually before
 * the supporting cloud function exists.
 *
 * WHY THIS IS GATED AND LOUD
 * A forecast-quality panel showing invented hit rates is exactly the sort of
 * thing that ends up in a screenshot and then in a slide. So:
 *   - it is OFF unless REACT_APP_ACCURACY_PLACEHOLDER=1, so it cannot ship by
 *     accident (CRA inlines env vars at build time — a production build without
 *     the var cannot render placeholders at all);
 *   - it only ever appears AFTER a real fetch has failed, so it disappears by
 *     itself the moment the endpoint works;
 *   - the component draws it dashed, dimmed and badged BEISPIELDATEN.
 *
 * The numbers are shaped to look plausible rather than flattering: hit rate
 * decays with horizon, the model track sits slightly below the message track,
 * MAE grows with horizon, and N+5 is deliberately absent to exercise the
 * missing-horizon path.
 */

import type { AccuracyCell, AccuracyNode } from './accuracyApi';

export const PLACEHOLDER_ENABLED =
  process.env.REACT_APP_ACCURACY_PLACEHOLDER === '1';

/** Stable per-scope variation, so S1 and S60 don't show identical numbers. */
function seedOf(key: string): number {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function round(x: number, nd: number): number {
  const f = Math.pow(10, nd);
  return Math.round(x * f) / f;
}

function cell(opts: {
  n: number;
  hit: number;
  modelHit: number;
  highActual: number;
  missRate: number;
  modelMissRate: number;
  mae: number;
}): AccuracyCell {
  return {
    n: opts.n,
    class_hit_rate: round(opts.hit, 4),
    n_high_actual: opts.highActual,
    high_to_low_miss_rate: round(opts.missRate, 4),
    mae_persons: round(opts.mae, 2),
    n_model: Math.round(opts.n * 0.98),
    model_class_hit_rate: round(opts.modelHit, 4),
    model_high_to_low_miss_rate: round(opts.modelMissRate, 4),
  };
}

/**
 * Build a synthetic node for one scope.
 *
 * @param key  scope identifier ('overall', a line code, or a journey id) —
 *             only used to vary the numbers deterministically.
 */
export function placeholderNode(key: string): AccuracyNode {
  const jitter = ((seedOf(key) % 9) - 4) / 100;   // -0.04 .. +0.04
  const scale = key === 'overall' ? 1 : 0.16;     // a line has fewer samples

  const node: AccuracyNode = {};
  let nTotal = 0;
  let hitTotal = 0;
  let modelNTotal = 0;
  let modelHitTotal = 0;
  let highTotal = 0;
  let missTotal = 0;
  let modelMissTotal = 0;
  let maeWeighted = 0;

  // N+5 intentionally omitted: an absent horizon must render as "—".
  for (let k = 1; k <= 4; k++) {
    const n = Math.round((5200 - k * 420) * scale);
    const hit = Math.min(0.985, Math.max(0.55, 0.945 - (k - 1) * 0.032 + jitter));
    const modelHit = Math.max(0.5, hit - 0.028);
    const highActual = Math.round(n * 0.11);
    const missRate = Math.min(0.4, 0.014 + (k - 1) * 0.011 + Math.abs(jitter) / 2);
    const modelMissRate = Math.max(0, missRate - 0.004);
    const mae = 7.4 + (k - 1) * 3.1;

    node[`N+${k}`] = cell({ n, hit, modelHit, highActual, missRate, modelMissRate, mae });

    nTotal += n;
    hitTotal += n * hit;
    const nModel = Math.round(n * 0.98);
    modelNTotal += nModel;
    modelHitTotal += nModel * modelHit;
    highTotal += highActual;
    missTotal += highActual * missRate;
    modelMissTotal += highActual * modelMissRate;
    maeWeighted += n * mae;
  }

  node.all = {
    n: nTotal,
    class_hit_rate: round(hitTotal / nTotal, 4),
    n_high_actual: highTotal,
    high_to_low_miss_rate: round(missTotal / highTotal, 4),
    mae_persons: round(maeWeighted / nTotal, 2),
    n_model: modelNTotal,
    model_class_hit_rate: round(modelHitTotal / modelNTotal, 4),
    model_high_to_low_miss_rate: round(modelMissTotal / highTotal, 4),
  };

  return node;
}
